import { useState, useEffect, useMemo } from 'react';
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import {
    Box, Typography, Paper, Chip, Stack, Button,
    CircularProgress, Alert, Tooltip, IconButton
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import Notifications from '@mui/icons-material/Notifications';
import StoreIcon from '@mui/icons-material/Store';
import { stateZipCodes } from '../utils/StateZipCodes';

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const StateSelector = ({ 
    selectedStates = [], 
    allStates = [], 
    onChange,
    onSave,
    onCancel,
    loading = false,
    saving = false,
    hasChanges = false,
    hideActions = false
}) => {
    const [localSelectedStates, setLocalSelectedStates] = useState(selectedStates);

    useEffect(() => {
        setLocalSelectedStates(selectedStates);
    }, [selectedStates]);

    const selectedStateIds = useMemo(() => 
        new Set(localSelectedStates.map(s => s.stateId)), 
        [localSelectedStates]
    );

    const handleStateClick = (stateName) => {
        if (loading || saving) return;

        const state = allStates.find(s => s.stateName === stateName);
        if (!state) return;

        let newSelection;
        if (selectedStateIds.has(state.stateId)) {
            newSelection = localSelectedStates.filter(s => s.stateId !== state.stateId);
        } else {
            newSelection = [...localSelectedStates, {
                stateId: state.stateId,
                stateName: state.stateName,
                stateCode: state.stateCode
            }];
        }

        setLocalSelectedStates(newSelection);
        onChange(newSelection);
    };

    const handleSelectAll = () => {
        if (loading || saving) return;
        
        const allSelected = allStates.map(state => ({
            stateId: state.stateId,
            stateName: state.stateName,
            stateCode: state.stateCode
        }));
        
        setLocalSelectedStates(allSelected);
        onChange(allSelected);
    };

    const handleClearAll = () => {
        if (loading || saving) return;
        
        setLocalSelectedStates([]);
        onChange([]);
    };

    const handleRemoveState = (stateId) => {
        if (loading || saving) return;
        
        const newSelection = localSelectedStates.filter(s => s.stateId !== stateId);
        setLocalSelectedStates(newSelection);
        onChange(newSelection);
    };

    const handleMakeThisYourStore = (zipCode) => {
        window.open('https://www.bestbuy.com/site/store-locator', '_blank', 'noopener,noreferrer');
    };

    const sortedSelectedStates = useMemo(() => 
        [...localSelectedStates].sort((a, b) => a.stateName.localeCompare(b.stateName)),
        [localSelectedStates]
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Paper elevation={hideActions ? 0 : 3} sx={{ p: hideActions ? 0 : 4, mb: hideActions ? 0 : 4 }}>
            {!hideActions && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Notifications sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">
                        Best Buy In-Store Notification Preferences
                    </Typography>
                </Box>
            )}

            {hasChanges && !hideActions && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    You have unsaved changes. Click "Save Preferences" below to apply them.
                </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body1" color="text.secondary">
                    <strong>{localSelectedStates.length}</strong> of <strong>{allStates.length}</strong> states selected
                </Typography>
                <Box>
                    <Tooltip title="Select All States">
                        <span>
                            <Button
                                size="small"
                                onClick={handleSelectAll}
                                disabled={loading || saving || localSelectedStates.length === allStates.length}
                                startIcon={<SelectAllIcon />}
                                sx={{ mr: 1 }}
                            >
                                Select All
                            </Button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Clear All States">
                        <span>
                            <Button
                                size="small"
                                onClick={handleClearAll}
                                disabled={loading || saving || localSelectedStates.length === 0}
                                startIcon={<DeselectIcon />}
                                color="secondary"
                            >
                                Clear All
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ 
                position: 'relative',
                height: 500,
                borderRadius: 2,
                overflow: 'hidden',
                border: '2px solid var(--sm-border-color)',
                mb: 3,
                bgcolor: 'var(--background-color-dark)'
            }}>
                <ComposableMap
                    projection="geoAlbersUsa"
                    projectionConfig={{
                        scale: 1000
                    }}
                    style={{
                        width: "100%",
                        height: "100%"
                    }}
                >
                    <Geographies geography={geoUrl}>
                        {({ geographies }) =>
                            geographies.map(geo => {
                                const stateName = geo.properties.name;
                                const state = allStates.find(s => s.stateName === stateName);
                                const isSelected = state && selectedStateIds.has(state.stateId);

                                return (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        strokeWidth={1}
                                        style={{
                                            /* SVG fill/stroke attributes can't resolve var(); use inline styles */
                                            default: {
                                                fill: isSelected ? "#5865F2" : "var(--sm-border-strong)",
                                                stroke: "var(--sm-map-stroke)",
                                                outline: "none",
                                                cursor: (loading || saving) ? 'default' : 'pointer'
                                            },
                                            hover: (loading || saving) ? {
                                                fill: isSelected ? "#5865F2" : "var(--sm-border-strong)",
                                                stroke: "var(--sm-map-stroke)"
                                            } : {
                                                fill: isSelected ? "#4752C4" : "var(--sm-muted-color)",
                                                stroke: "var(--sm-map-stroke)",
                                                outline: "none",
                                                cursor: "pointer"
                                            },
                                            pressed: {
                                                fill: isSelected ? "#5865F2" : "var(--sm-border-strong)",
                                                stroke: "var(--sm-map-stroke)",
                                                outline: "none"
                                            }
                                        }}
                                        onClick={() => handleStateClick(stateName)}
                                    />
                                );
                            })
                        }
                    </Geographies>
                </ComposableMap>

                <Box sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: 16,
                    display: 'flex',
                    gap: 3,
                    bgcolor: 'var(--sm-overlay-bg)',
                    p: 1.5,
                    borderRadius: 1,
                    boxShadow: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 20, height: 20, bgcolor: '#5865F2', mr: 1, borderRadius: 0.5 }} />
                        <Typography variant="body2" fontWeight="500">Selected</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: 20, height: 20, bgcolor: 'var(--sm-border-strong)', mr: 1, borderRadius: 0.5 }} />
                        <Typography variant="body2" fontWeight="500">Not Selected</Typography>
                    </Box>
                </Box>
            </Box>

            {localSelectedStates.length === 0 && !hideActions && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    No states selected. You will not receive any in-store pickup notifications. Select at least one state to receive alerts.
                </Alert>
            )}

            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Selected States ({localSelectedStates.length})
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: hideActions ? 0 : 3 }}>
                {sortedSelectedStates.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        No states selected
                    </Typography>
                ) : (
                    sortedSelectedStates.map((state) => {
                        const zipCode = stateZipCodes[state.stateName];
                        return (
                            <Chip
                                key={state.stateId}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <span>{state.stateName} ({zipCode || state.stateCode})</span>
                                        <Tooltip title="Set your preferred Best Buy store for this state">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMakeThisYourStore(zipCode);
                                                }}
                                                sx={{ 
                                                    padding: 0,
                                                    ml: 0.5,
                                                    color: 'primary.main',
                                                    '&:hover': {
                                                        color: 'primary.dark'
                                                    }
                                                }}
                                            >
                                                <StoreIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                }
                                onDelete={(loading || saving) ? undefined : () => handleRemoveState(state.stateId)}
                                deleteIcon={<ClearIcon />}
                                color="primary"
                                variant="outlined"
                                sx={{
                                    height: 'auto',
                                    py: 0.5,
                                    '& .MuiChip-label': {
                                        display: 'flex',
                                        alignItems: 'center'
                                    }
                                }}
                            />
                        );
                    })
                )}
            </Stack>

            {hasChanges && !hideActions && (
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={saving}
                        size="large"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={onSave}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} /> : null}
                        size="large"
                    >
                        {saving ? 'Saving...' : 'Save Preferences'}
                    </Button>
                </Box>
            )}
        </Paper>
    );
};

export default StateSelector;